import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
	deletePerson,
	isThereInvestmentWorkflowPerson,
} from '@dealstack/frontend/app/services/Person.service.ts';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	selectedEntities: Set<string> | string[];
	alertTitle?: string;
	alertDescription?: string;
	confirmDeleteText?: string;
	inputLabel?: string;
	inputPlaceholder?: string;
	refreshEntitiesLists: () => void;
	resetSelectedEntities?: () => void;
	forceDelete?: boolean;
}

const DeleteEntityDrawer = ({
	opened,
	selectedEntities,
	onClose,
	refreshEntitiesLists,
	resetSelectedEntities,
	alertTitle,
	alertDescription,
	confirmDeleteText,
	inputLabel,
	inputPlaceholder,
	forceDelete = false,
}: Props) => {
	const [isTsaExist, setIsTsaExist] = useState<boolean>(false);
	const tCapManage = useTranslationCapManage();

	const { handleEntityDeletionNavigation } = useContext(CapManageControllerContext);

	const deleteEntitiesRequest = useMutation({
		mutationFn: deletePerson,
	});

	const isThereInvestmentWorkflow = useMutation({
		mutationFn: isThereInvestmentWorkflowPerson,
	});

	const selectedEntitiesIds = useMemo(() => {
		return Array.from(selectedEntities);
	}, [selectedEntities]);

	const handleConfirmDelete = async () => {
		await deleteEntitiesRequest.mutateAsync(
			{ deletePersonDto: selectedEntitiesIds, force: forceDelete },
			{
				onSuccess: async () => {
					showToastV2({ message: tCapManage('entity.delete.toast.success') });

					await handleEntityDeletionNavigation(selectedEntitiesIds);

					refreshEntitiesLists();
					onClose();
					resetSelectedEntities && resetSelectedEntities();
				},
			}
		);
	};

	const fetchTsaData = async (ids: string[]) => {
		const data = await isThereInvestmentWorkflow.mutateAsync(ids);
		setIsTsaExist(!!data?.isTsaExist);
	};

	useEffect(() => {
		if (!opened || !selectedEntitiesIds.length) {
			return;
		}
		void fetchTsaData(selectedEntitiesIds);
	}, [selectedEntitiesIds, opened]);

	return (
		<ConfirmDeleteDrawer
			opened={opened}
			onClose={onClose}
			title={tCapManage('entity.delete.drawer.title')}
			alertTitle={
				isTsaExist && !forceDelete
					? tCapManage('delete.entity.drawer.forbiddenTitle')
					: alertTitle || tCapManage('delete.entity.drawer.title')
			}
			alertDescription={
				isTsaExist && !forceDelete
					? tCapManage('delete.entity.drawer.forbiddenDescription')
					: alertDescription || tCapManage('delete.entity.drawer.description')
			}
			inputLabel={inputLabel || tCapManage('delete.entity.drawer.inputLabel')}
			inputPlaceholder={
				inputPlaceholder || tCapManage('delete.entity.drawer.inputPlaceholder')
			}
			hideAlertIcon={true}
			confirmDeleteText={
				confirmDeleteText || tCapManage('delete.entity.drawer.deleteSelection')
			}
			wordMismatchErrorMessage={tCapManage('delete.entity.drawer.error.wordMismatch')}
			deleteBtnText={tCapManage('delete.entity.drawer.confirmDelete')}
			cancelBtnText={tCapManage('delete.entity.drawer.cancel')}
			deleteSuccess={deleteEntitiesRequest.isSuccess}
			deleteLoading={deleteEntitiesRequest.isPending}
			handleDelete={handleConfirmDelete}
			tsaConnected={isTsaExist && !forceDelete}
		/>
	);
};
const _DeleteEntityDrawerExport = memo(DeleteEntityDrawer);

export { _DeleteEntityDrawerExport as DeleteEntityDrawer };
